import { LogoSvg } from "components/svg"
import Link from "next/link"
import { useMe } from "context/user"
import React from "react"
import Router from "next/router"
import { firebase } from "../firebase"
import { css } from "utils"
import { log } from "../log"
import Head from "next/head"
import { ToggleOutline } from "components/toggle"
import { GoogleLoginButton } from "components/googleLogin"
import { useAsyncState } from "hooks"

const Index = () => {
  const { data, isLoading } = useMe()
  const [email, setEmail] = React.useState("")
  const [emailSent, setEmailSent] = React.useState(false)
  const [signup, { loading: signupLoading, error: signupError }] =
    useAsyncState(async () => {
      await firebase.auth().sendSignInLinkToEmail(email, {
        handleCodeInApp: true,
        url: `${window.location.origin}/sign-up?email=${encodeURIComponent(
          email
        )}`,
      })

      setEmailSent(true)
    })

  React.useEffect(() => {
    if (!isLoading && data) {
      log.event("Sign In User")
      const mostRecentPage = data.activeAdventure?.mostRecentPage
      Router.replace(`/page/${mostRecentPage.id}`)
    }
  }, [isLoading, data])

  if (isLoading || (!isLoading && data)) {
    return null
  }

  return (
    <div className="text-gray-200">
      <Head>
        <meta
          name="Description"
          content="Easily create fantastic adventures and run your games with confidence"
        />
      </Head>
      <div className="relative flex text-gray-200">
        <div
          style={{
            minHeight: 650,
            backgroundImage: "url(static/fantasy-castle.webp)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPositionY: "45%",
          }}
          className="relative w-full h-full px-4 z-10 pt-4 pb-12 md:pt-8 md:pr-8 md:pr-16 md:pl-8"
        >
          <div className="sm:flex justify-between mb-8">
            <div className="flex items-center justify-between">
              <div className="text-2xl flex items-center">
                <LogoSvg className="mr-2" height={32} width={32} />{" "}
                <span>Adventurekeep</span>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-2 sm:mt-0">
              <a
                className="no-underline hover:underline text-gray-200"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.notion.so/Adventurekeep-Help-Support-340ae52820fa49fbadbe0beb9c97235e"
              >
                Help Center
              </a>
              <a
                className="no-underline hover:underline text-gray-200"
                href="/#pricing"
              >
                Pricing
              </a>

              <Link
                className="hover:underline no-underline text-gray-200"
                href="/sign-in"
              >
                Sign in
              </Link>
            </div>
          </div>

          <div className="text-gray-200 w-full lg:w-3/5 mx-auto text-center">
            <div className="text-4xl font-bold mb-2">
              The 5th Edition Campaign Builder
            </div>
            <p className="text-xl font-light mb-12">
              Easily create adventures and run games your players will love
            </p>

            <p className="mb-4 font-semibold uppercase tracking-wide">
              Get started for free
            </p>
            {signupError && (
              <p className="text-red-600">
                There was an error signing up. Please try again, or contact us.
              </p>
            )}
            {emailSent && (
              <p>
                We just sent you an email. Check your inbox (possibly junk
                mail!) to continue.
              </p>
            )}
            {!(signupError || emailSent) && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault()
                  signup()
                }}
              >
                <div className="flex-col sm:w-3/4 mx-auto md:flex-row flex">
                  <input
                    aria-label="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    name="EMAIL"
                    required
                    type="email"
                    placeholder="Enter your email"
                    className="text-gray-800 text-lg bg-gray-200 p-2 rounded mb-2 md:mr-2 shadow-lg flex-1"
                  />
                  <button
                    disabled={signupLoading}
                    type="submit"
                    className={css(
                      signupLoading && "opacity-75 cursor-not-allowed",
                      "mb-2 shadow-lg tracking-wide rounded bg-blue-700 px-4 py-2 text-gray-200 font-semibold"
                    )}
                  >
                    Sign Up
                  </button>
                </div>
              </form>
            )}
            <div className="text-center mb-2">or</div>
            <div className="flex flex-1 justify-center">
              <Link
                onClick={() => {
                  log.event("Clicked Continue With Google")
                }}
                className="no-underline"
                href="/google-sign-in"
              >
                <GoogleLoginButton />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='540' height='450' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.01'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3Cpolygon points='630 150 540 300 720 300'/%3E%3Cpolygon fill='%23DDD' points='630 150 720 0 540 0'/%3E%3Cpolygon fill='%23444' points='810 150 720 300 900 300'/%3E%3Cpolygon fill='%23FFF' points='810 150 900 0 720 0'/%3E%3Cpolygon fill='%23DDD' points='990 150 900 300 1080 300'/%3E%3Cpolygon fill='%23444' points='990 150 1080 0 900 0'/%3E%3Cpolygon fill='%23DDD' points='90 450 0 600 180 600'/%3E%3Cpolygon points='90 450 180 300 0 300'/%3E%3Cpolygon fill='%23666' points='270 450 180 600 360 600'/%3E%3Cpolygon fill='%23AAA' points='270 450 360 300 180 300'/%3E%3Cpolygon fill='%23DDD' points='450 450 360 600 540 600'/%3E%3Cpolygon fill='%23999' points='450 450 540 300 360 300'/%3E%3Cpolygon fill='%23999' points='630 450 540 600 720 600'/%3E%3Cpolygon fill='%23FFF' points='630 450 720 300 540 300'/%3E%3Cpolygon points='810 450 720 600 900 600'/%3E%3Cpolygon fill='%23DDD' points='810 450 900 300 720 300'/%3E%3Cpolygon fill='%23AAA' points='990 450 900 600 1080 600'/%3E%3Cpolygon fill='%23444' points='990 450 1080 300 900 300'/%3E%3Cpolygon fill='%23222' points='90 750 0 900 180 900'/%3E%3Cpolygon points='270 750 180 900 360 900'/%3E%3Cpolygon fill='%23DDD' points='270 750 360 600 180 600'/%3E%3Cpolygon points='450 750 540 600 360 600'/%3E%3Cpolygon points='630 750 540 900 720 900'/%3E%3Cpolygon fill='%23444' points='630 750 720 600 540 600'/%3E%3Cpolygon fill='%23AAA' points='810 750 720 900 900 900'/%3E%3Cpolygon fill='%23666' points='810 750 900 600 720 600'/%3E%3Cpolygon fill='%23999' points='990 750 900 900 1080 900'/%3E%3Cpolygon fill='%23999' points='180 0 90 150 270 150'/%3E%3Cpolygon fill='%23444' points='360 0 270 150 450 150'/%3E%3Cpolygon fill='%23FFF' points='540 0 450 150 630 150'/%3E%3Cpolygon points='900 0 810 150 990 150'/%3E%3Cpolygon fill='%23222' points='0 300 -90 450 90 450'/%3E%3Cpolygon fill='%23FFF' points='0 300 90 150 -90 150'/%3E%3Cpolygon fill='%23FFF' points='180 300 90 450 270 450'/%3E%3Cpolygon fill='%23666' points='180 300 270 150 90 150'/%3E%3Cpolygon fill='%23222' points='360 300 270 450 450 450'/%3E%3Cpolygon fill='%23FFF' points='360 300 450 150 270 150'/%3E%3Cpolygon fill='%23444' points='540 300 450 450 630 450'/%3E%3Cpolygon fill='%23222' points='540 300 630 150 450 150'/%3E%3Cpolygon fill='%23AAA' points='720 300 630 450 810 450'/%3E%3Cpolygon fill='%23666' points='720 300 810 150 630 150'/%3E%3Cpolygon fill='%23FFF' points='900 300 810 450 990 450'/%3E%3Cpolygon fill='%23999' points='900 300 990 150 810 150'/%3E%3Cpolygon points='0 600 -90 750 90 750'/%3E%3Cpolygon fill='%23666' points='0 600 90 450 -90 450'/%3E%3Cpolygon fill='%23AAA' points='180 600 90 750 270 750'/%3E%3Cpolygon fill='%23444' points='180 600 270 450 90 450'/%3E%3Cpolygon fill='%23444' points='360 600 270 750 450 750'/%3E%3Cpolygon fill='%23999' points='360 600 450 450 270 450'/%3E%3Cpolygon fill='%23666' points='540 600 630 450 450 450'/%3E%3Cpolygon fill='%23222' points='720 600 630 750 810 750'/%3E%3Cpolygon fill='%23FFF' points='900 600 810 750 990 750'/%3E%3Cpolygon fill='%23222' points='900 600 990 450 810 450'/%3E%3Cpolygon fill='%23DDD' points='0 900 90 750 -90 750'/%3E%3Cpolygon fill='%23444' points='180 900 270 750 90 750'/%3E%3Cpolygon fill='%23FFF' points='360 900 450 750 270 750'/%3E%3Cpolygon fill='%23AAA' points='540 900 630 750 450 750'/%3E%3Cpolygon fill='%23FFF' points='720 900 810 750 630 750'/%3E%3Cpolygon fill='%23222' points='900 900 990 750 810 750'/%3E%3Cpolygon fill='%23222' points='1080 300 990 450 1170 450'/%3E%3Cpolygon fill='%23FFF' points='1080 300 1170 150 990 150'/%3E%3Cpolygon points='1080 600 990 750 1170 750'/%3E%3Cpolygon fill='%23666' points='1080 600 1170 450 990 450'/%3E%3Cpolygon fill='%23DDD' points='1080 900 1170 750 990 750'/%3E%3C/g%3E%3C/svg%3E\")",
        }}
        className="bg-gray-850 p-4 pt-16"
      >
        <AllYourContent />
        <CreateMaps />
        <TrackEncounters />
        <ShareContent />
      </div>
      <PricingContent />
      <footer className="bg-gray-900 p-8 flex items-center justify-center flex-wrap text-xs space-x-4">
        <a
          href="mailto:contact@adventurekeep.com?subject=Contact Us"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-gray-200"
        >
          Contact
        </a>
        <a
          href="https://www.facebook.com/adventurekeep/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-gray-200"
        >
          Facebook
        </a>
        <a className="text-gray-200" href="/privacy">
          Privacy Policy
        </a>
        <a className="text-gray-200" href="/terms">
          Terms of Use
        </a>
        <a className="text-gray-200" href="https://thegildedteafling.com">
          Check out our tea!
        </a>
        <span>© Adventurekeep {new Date().getFullYear()}</span>
      </footer>
    </div>
  )
}

export default Index

const PricingContent = () => {
  const [isAnnual, setIsAnnual] = React.useState(true)

  return (
    <div className="px-2 sm:px-24 py-8 max-w-5xl mx-auto">
      <h3 id="pricing" className="text-center mb-2 px-4 text-3xl">
        Pricing
      </h3>
      <p className="text-center px-4 mb-8 text-lg text-gray-300 uppercase tracking-wide">
        Start your next adventure today
      </p>
      <div className="px-4 flex items-center md:justify-end">
        <button onClick={() => setIsAnnual(false)}>monthly billing</button>
        <ToggleOutline
          className="mx-2"
          value={isAnnual}
          onChange={setIsAnnual}
        />
        <button onClick={() => setIsAnnual(true)}>annual billing</button>
      </div>
      <div className="p-4 sm:flex -mx-2">
        <div className="p-6 mb-4 sm:mb-0 sm:w-1/2 flex flex-col bg-gray-900 mx-2 rounded shadow-lg">
          <div className="text-2xl">Free</div>
          <div className="text-lg">$0 forever</div>
          <hr className="my-2 border-gray-600" />
          <div className="text-sm mb-2">
            <div className="mb-2">1 Adventure</div>
            <div className="mb-2">Unlimited Pages</div>
            <div className="mb-2">5 Map Markers per Page</div>
            <div className="mb-2">1 MB Max Image Size</div>
            <div className="mb-2">100 MB Image Storage</div>
          </div>
          <a
            className="bg-blue-700 text-gray-200 rounded text-center px-4 py-2 no-underline"
            href="/#"
          >
            Start For Free
          </a>
        </div>
        <div className="p-6 sm:w-1/2 flex flex-col mx-2 bg-gray-900 rounded shadow-lg">
          <div className="text-2xl">Pro</div>
          <div className="flex items-center flex-wrap">
            <div className="text-lg mr-2">
              {isAnnual ? "$5" : "$8"} per month
            </div>
            {isAnnual && <div className="text-sm">($60 per year)</div>}
          </div>
          <hr className="my-2 border-gray-600" />
          <div className="text-sm mb-2">
            <div className="mb-2">Unlimited Adventures</div>
            <div className="mb-2">Unlimited Pages</div>
            <div className="mb-2">Unlimited Map Markers</div>
            <div className="mb-2">5 MB Max Image Size</div>
            <div className="mb-2">5 GB Image Storage</div>
          </div>
          <a
            className="bg-blue-700 text-gray-200 rounded text-center px-4 py-2 no-underline"
            href="/#"
          >
            Start For Free
          </a>
        </div>
      </div>
    </div>
  )
}

const AllYourContent = () => (
  <div className="flex flex-col md:flex-row justify-center container mb-32 mx-auto md:space-x-12">
    <div className="md:w-1/3">
      <div className="text-3xl font-semibold mb-4 mt-4 md:mt-0">
        All your content in one place
      </div>
      <div className="mb-4 text-gray-300 text-xl leading-relaxed">
        Easily organize your notes, link your content together, and quickly find
        spells, monsters, and more. Your entire campaign is at your fingertips.
      </div>
    </div>
    <div className="flex md:w-1/2 justify-center items-start">
      <video
        src="static/organize-notes.mp4"
        playsInline
        autoPlay
        loop
        className="object-contain rounded mx-4 shadow-lg"
      />
    </div>
  </div>
)

const CreateMaps = () => (
  <div className="flex flex-col flex-col-reverse md:flex-row justify-center container mb-32 mx-auto md:space-x-12">
    <div className="flex md:w-1/2 justify-center items-start">
      <video
        src="static/interactive-map.mp4"
        playsInline
        autoPlay
        loop
        className="object-contain rounded mx-4 shadow-lg"
      />
    </div>
    <div className="md:w-1/3">
      <div className="text-3xl font-semibold mb-4 mt-4 md:mt-0">
        Create interactive maps
      </div>
      <div className="mb-4 text-gray-300 text-xl leading-relaxed">
        Select beautiful, professional maps from the gallery, or upload your
        own. Add markers and points of interest to create dungeons, cities,
        continents, or whatever your mind can conjure.
      </div>
    </div>
  </div>
)

const TrackEncounters = () => (
  <div className="flex flex-col md:flex-row justify-center container mb-32 mx-auto md:space-x-12">
    <div className="md:w-1/3">
      <div className="text-3xl font-semibold mb-4 mt-4 md:mt-0">
        Build & run encounters
      </div>
      <div className="mb-4 text-gray-300 text-xl leading-relaxed">
        Create encounters and custom monsters for your party in seconds,
        streamline running encounters, and never stress over encounter
        management again.
      </div>
    </div>
    <div className="flex md:w-1/2 justify-center items-start">
      <video
        src="static/encounter-management.mp4"
        loop
        autoPlay
        playsInline
        className="object-contain rounded mx-4 shadow-lg"
      />
    </div>
  </div>
)

const ShareContent = () => (
  <div className="flex flex-col md:flex-row flex-col-reverse justify-center container mb-32 mx-auto md:space-x-12">
    <div className="flex md:w-1/3 justify-center items-start">
      <video
        loop
        autoPlay
        playsInline
        src="static/share-pages.mp4"
        className="object-contain rounded mx-4 shadow-lg"
      />
    </div>
    <div className="md:w-1/3">
      <div className="text-3xl font-semibold mb-4 mt-4 md:mt-0">
        Share your content
      </div>
      <div className="mb-4 text-gray-300 text-xl leading-relaxed">
        Create handouts, notes, maps, and more and share them directly with your
        party.
      </div>
    </div>
  </div>
)
